import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import { distinctUntilChanged, map, of, Subscription, switchMap } from 'rxjs';
import { StateService } from '@uirouter/core';

import { VbConfirmationDialogComponent } from 'rev-shared/ui/dialog/VbConfirmationDialogAngular.Component';
import { isMobile } from 'rev-shared/util/UserAgentUtil';
import { IProducerLayout } from 'rev-shared/webcast/webcastView/producer/Contract';
import { SelfProducedService } from 'rev-shared/webcast/webcastView/selfProduced/SelfProduced.Service';
import { WebRtcListenerConnectionService } from 'rev-shared/webrtc/WebRtcListenerConnection.Service';

import { WebcastModel } from '../model/WebcastModel';
import { WebcastService } from '../Webcast.Service';

import styles from './WebcastNav.module.less';
import { IWebcastViewContext } from 'rev-shared/webcast/webcastView/IWebcastViewContext';

@Component({
	selector: 'webcast-nav',
	templateUrl: './WebcastNav.Component.html',
	host: {
		'[class]': 'hostClass'
	},
	standalone: false
})
export class WebcastNavComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public webcast: WebcastModel;
	@Input() public backstageLayout: IProducerLayout;
	@Input() public onStageLayout: IProducerLayout;
	@Input() public previousState: { name: string; params: any; };
	@Input() public webcastViewContext: IWebcastViewContext;

	@Output() public onEndEvent: EventEmitter<any> = new EventEmitter();

	@ViewChild(VbConfirmationDialogComponent)
	public manualControlError: VbConfirmationDialogComponent;

	public readonly styles = styles;
	public hostClass = `${this.styles.root} theme-primary theme-primary-font-border-fade-50`;
	public disablePresenterReady: boolean;
	private sub: Subscription;
	public producerStatus: { isBackstage?: boolean, isLive?: boolean } = {};
	public isMobile = isMobile();

	constructor(
		private readonly $state: StateService,
		private readonly WebcastService: WebcastService,
		public readonly SelfProducedService: SelfProducedService,
		private readonly WebRtcListener: WebRtcListenerConnectionService
	) {}

	public ngOnInit(): void {
		this.sub = this.webcast.webcast$.pipe(
			map(w => w.isProducerEvent &&
				w.currentUser.isPresenter &&
				this.WebRtcListener.presenters?.presenters$),
			distinctUntilChanged(),
			switchMap(presenters$ => presenters$ || of())
		)
			.subscribe(() => this.updateProducerStatus());
	}

	public ngOnChanges(): void {
		this.updateProducerStatus();
	}

	public ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	public cancelAutomatedWebcast(): void {
		this.WebcastService.cancelScheduledAutomatedWebcast(this.webcast.id, this.webcast.currentRun.runNumber)
			.catch(() => this.manualControlError.open())
			.then(() => this.webcast.update());
	}

	public endEvent(): void {
		this.onEndEvent.emit();
	}

	public toggleLayout(): void {
		this.webcast.layout.isVertical = !this.webcast.layout.isVertical;
	}

	public showPresenterReadyToggle(): boolean {
		return this.webcast.currentUser.isSinglePresenter &&
			this.webcast.webcastStatus.isStarted &&
			!this.webcast.broadcast.isActive &&
			!this.webcast.broadcast.isStarting;
	}

	public onPresenterReadyChange(): void {
		this.disablePresenterReady = true;
		this.SelfProducedService.setPresenterReady(this.webcast.id, this.webcast.isPresenterReady)
			.then(() => this.disablePresenterReady = false);
	}

	public getPresenterLabel(): string {
		return this.webcast.broadcast.isActive ? 'Broadcasting' :
			this.webcast.broadcast.hasBeenActive ? 'Event_Status_BroadcastPaused' :
			this.webcast.webcastStatus.isInProgress ? 'Event_Status_BroadcastReady' :
			'Event_Status_BroadcastNotReady';
	}

	private updateProducerStatus(): void {
		this.producerStatus = this.webcast.isProducerEvent && this.webcast.currentUser.isPresenter &&
			this.getPresenterStatus()
			|| {};
	}

	public getPresenterStatus(): { isLive, isBackstage } {
		const userId = this.webcast.currentUser.id;
		const presenters = this.WebRtcListener.presenters;
		const layoutHasUser = (layout: IProducerLayout) => presenters?.getLayoutPresenters(layout).some(p => p.id === userId);

		const isLive = layoutHasUser(this.onStageLayout);
		return {
			isLive,
			isBackstage: !isLive && layoutHasUser(this.backstageLayout)
		};
	}

	public goToPreviousState(): void {
		if (this.previousState) {
			this.$state.transitionTo(this.previousState.name, this.previousState.params);
		}
	}
}
